import { IPromise, IRootScopeService } from "angular";
import { CommentService } from "@gtmhub/comments/services/comment.service";
import { UIErrorHandlingService } from "@gtmhub/error-handling";
import { GoalService } from "@gtmhub/goals/services/goal.service";
import { localize } from "@gtmhub/localization";
import { OkrEvents } from "@gtmhub/okrs/events";
import { AnalyticsService } from "@webapp/analytics/services/analytics.service";
import { CommentTargetType, CreateCommentDTO } from "@webapp/comments";
import { IOkrClosedStatus, OkrStatus } from "@webapp/okrs/goals/models/goal-status.models";
import { IOKRModalIndicators } from "./models";

export function addPrefixToCommentText(comment: CreateCommentDTO, newState: OkrStatus): CreateCommentDTO {
  return {
    ...comment,
    text: comment.text ? `${localize("state_of_objective_changed_to")} ${localize(newState)}: ${comment.text}` : undefined,
  };
}

export class CloseOkrService {
  private okrEvents: OkrEvents;
  static $inject = ["$rootScope", "CommentService", "UIErrorHandlingService", "GoalService", "AnalyticsService"];

  constructor(
    private $rootScope: IRootScopeService,
    private commentService: CommentService,
    private uiErrorHandlingService: UIErrorHandlingService,
    private goalService: GoalService,
    private analyticsService: AnalyticsService
  ) {
    this.okrEvents = new OkrEvents(this.$rootScope);
  }

  initComment(args: { targetId: string; targetTitle: string; targetType: "goal" }): CreateCommentDTO {
    return {
      targetId: args.targetId,
      targetTitle: args.targetTitle,
      targetType: args.targetType,
      mentioned: [],
      text: "",
    };
  }

  broadcastCommentPostedExternally(targetId: string, targetType: CommentTargetType): void {
    this.$rootScope.$broadcast("commentPostedExternally", targetId, targetType);
  }

  postComment(args: { indicators: IOKRModalIndicators; comment: CreateCommentDTO; targetId: string; targetType: CommentTargetType; callback(): void }): void {
    args.indicators.creatingComment = { progress: true };
    this.commentService
      .post(args.comment)
      .then(() => {
        delete args.indicators.creatingComment;
        this.broadcastCommentPostedExternally(args.targetId, args.targetType);
      })
      .catch((error) => {
        this.uiErrorHandlingService.handleModal(error);
      })
      .finally(() => args.callback());
  }

  closeOkr(args: { goalId: string; selectedStatus: OkrStatus; reason: string }): IPromise<void> {
    const closeOkr: IOkrClosedStatus = {
      status: args.selectedStatus,
      reason: args.reason,
    };

    return this.goalService.closeGoal(args.goalId, closeOkr).then(() => {
      this.okrEvents.broadcastGoalsChanged("goalStatusChanged", { goalId: args.goalId, status: args.selectedStatus });
    });
  }

  reopenOkr(goalId: string): IPromise<void> {
    return this.goalService.reopenGoal(goalId).then(() => {
      this.okrEvents.broadcastGoalsChanged("goalStatusChanged", { goalId: goalId, status: "active" });
    });
  }

  /* eslint-disable @typescript-eslint/naming-convention */
  trackGoalClosedStatusChange(meta: { new_state: OkrStatus; old_state: OkrStatus; comment_posted: boolean; reason?: string }): void {
    this.analyticsService.track("Objective State Changed", meta);
  }
}
